import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { ChangeRequestView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { changeRequestFieldLabelKeys, changeRequestFieldNames } from './changeRequests';

export interface ChangeRequestsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

/** The review queue for secretaries: current and proposed values side by side. */
export function ChangeRequestsPanel({
  locale,
  organizationId,
  csrfToken,
}: ChangeRequestsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const [requests, setRequests] = useState<ChangeRequestView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setRequests(await apiRequest<ChangeRequestView[]>(`${base}/change-requests?status=pending`));
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(request: ChangeRequestView, decision: 'approve' | 'reject'): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest(`${base}/change-requests/${request.id}/decision`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: request.version,
        body: decision === 'approve' ? { decision } : { decision, reason: reason.trim() },
      });
      setRejecting(null);
      setReason('');
      await load();
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  if (requests?.length === 0 && errorKey === null) return <></>;

  return (
    <Box component="section" aria-labelledby="change-requests-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="change-requests-title" gutterBottom>
        {translate(locale, 'changeRequests.review.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {requests?.map((request) => (
        <Box key={request.id} sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
          <Typography>
            {request.athleteName}
            {request.sensitive && ` — ${translate(locale, 'changeRequests.sensitive')}`}
          </Typography>
          {changeRequestFieldNames(request.proposed).map((field) => (
            <Typography key={field} variant="body2" color="text.secondary">
              {translate(locale, changeRequestFieldLabelKeys[field])}:{' '}
              {request.current?.[field] ?? '—'} → {request.proposed[field] ?? '—'}
            </Typography>
          ))}
          {request.note !== null && <Typography variant="body2">{request.note}</Typography>}
          {rejecting === request.id ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label={translate(locale, 'changeRequests.reason')}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
              <Button
                size="small"
                color="error"
                disabled={reason.trim() === ''}
                onClick={() => {
                  void decide(request, 'reject');
                }}
              >
                {translate(locale, 'changeRequests.reject.confirm')}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  void decide(request, 'approve');
                }}
              >
                {translate(locale, 'changeRequests.approve')}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setRejecting(request.id);
                }}
              >
                {translate(locale, 'changeRequests.reject')}
              </Button>
            </Stack>
          )}
        </Box>
      ))}
    </Box>
  );
}
